/**
 * Домашка без заданий и её отметка «Сделал(а)» (1.3 и 1.5
 * `docs/homework-scratch-design.md`). Чистые функции и тексты ленты ученика,
 * отдельно от разметки, чтобы решение «есть ли кнопка» и «можно ли отменить»
 * проверялось тестом, а не глазами.
 *
 * ⚠️ Правило выполненности живёт в семи местах, и разъехаться им нельзя (1.5
 * проекта). Здесь его НЕТ: статус считает `homeworkState` из `@edu/shared`, а
 * этот модуль решает только, что показать рядом с кнопкой.
 */

interface ExerciseLike {
  voidedAt: number | null;
}

/** Задания, которые считаются: неаннулированные. */
export function liveExercises(exercises: readonly ExerciseLike[]): number {
  return exercises.filter((ex) => ex.voidedAt === null).length;
}

/**
 * Кнопка «Сделал(а)» есть только у домашки без заданий, то есть без
 * НЕаннулированных: смешанная, у которой сняли все задания, сюда тоже попадает,
 * иначе она была бы вечно не сделана.
 */
export function showDoneButton(exercises: readonly ExerciseLike[]): boolean {
  return liveExercises(exercises) === 0;
}

/**
 * Можно ли снять отметку: пока не прошёл срок, пока не приложено ни одного
 * фото и пока не отправлен ответ текстом (1.8 проекта). Сервер проверяет то же
 * самое в своём `UPDATE`; здесь это только о том, рисовать ли «Отменить».
 */
export function doneUndoable(args: {
  studentDoneAt: number | null;
  dueAt: number | null;
  photos: number;
  /** Момент отправки ответа текстом; `null` означает «не отправлен». */
  answerAt: number | null;
  now: number;
}): boolean {
  if (args.studentDoneAt === null || args.photos > 0 || args.answerAt !== null) return false;
  return args.dueAt === null || args.now <= args.dueAt;
}

/**
 * Строка у смешанной домашки: материал плюс задания. Без неё приложенное фото
 * раздатки читается как сдача, а сделанной такую домашку делают ответы.
 */
export function mixedNoteShown(args: {
  exercises: readonly ExerciseLike[];
  hasMaterial: boolean;
}): boolean {
  return args.hasMaterial && liveExercises(args.exercises) > 0;
}

export const MIXED_NOTE = "Домашка будет сдана, когда на все задания будут ответы.";
export const DONE_BUTTON = "Сделал(а)";
export const DONE_MARKED = "Отмечено: домашка сдана.";
export const DONE_WAITING = "Отметка не сохранилась. Сохраним сами, когда появится связь.";
/** То же для снятия отметки: «не сохранилась» про снятие читалось бы наоборот. */
export const UNDONE_WAITING = "Отметка не снялась. Снимем сами, когда появится связь.";

/**
 * Обрыв это повтор, а не отказ: сеть (`null`), пятисотые и частота. Остальное
 * (404 у удалённой домашки) повторять бессмысленно.
 */
export function doneRetryable(status: number | null): boolean {
  return status === null || status >= 500 || status === 429;
}

/**
 * Слияние списка фото после перечитывания карточки: то, что приехало с
 * сервера, плюс приложенные на этом экране, которых в ответе ещё нет (ответ
 * мог уйти до того, как отправка закончилась). Ключ это `fileId`: одно фото
 * дважды в ряду читается как «приложилось два».
 */
export function mergePhotos<T extends { fileId: string }>(
  server: readonly T[],
  local: readonly T[],
  addedHere: ReadonlySet<string>,
): T[] {
  const known = new Set(server.map((p) => p.fileId));
  return [...server, ...local.filter((p) => addedHere.has(p.fileId) && !known.has(p.fileId))];
}

/** Приложить одно фото к списку, не задваивая уже известное. */
export function appendPhoto<T extends { fileId: string }>(list: readonly T[], photo: T): T[] {
  return list.some((p) => p.fileId === photo.fileId) ? [...list] : [...list, photo];
}
